/**
 * Modal para ver el historial de asistencia de un miembro
 */

import { useMemo, useState } from "react";
import { Button, Badge, Select } from "@/components/ui";
import { Card, CardContent } from "@/components/ui";
import {
  X,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  TrendingUp,
  Download,
} from "lucide-react";
import {
  useAttendanceByMember,
  useMemberAttendanceStats,
} from "@/hooks/useAttendance";
import { format, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import type { Member, AttendanceStatus } from "@/types";
import { Timestamp } from "firebase/firestore";
import { exportToXlsx } from "@/lib/utils/exportXlsx";

interface AttendanceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  personName: string;
}

const STATUS_CONFIG: Record<
  AttendanceStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  present: {
    label: "Presente",
    color: "bg-green-100 text-green-800",
    icon: <CheckCircle className="w-4 h-4 text-green-600" />,
  },
  absent: {
    label: "Ausente",
    color: "bg-red-100 text-red-800",
    icon: <XCircle className="w-4 h-4 text-red-600" />,
  },
  late: {
    label: "Tardanza",
    color: "bg-yellow-100 text-yellow-800",
    icon: <Clock className="w-4 h-4 text-yellow-600" />,
  },
  excused: {
    label: "Excusado",
    color: "bg-blue-100 text-blue-800",
    icon: <AlertCircle className="w-4 h-4 text-blue-600" />,
  },
};

type FilterPeriod = "all" | "1month" | "3months" | "6months" | "1year";

function getDateRange(period: FilterPeriod): {
  start?: Timestamp;
  end?: Timestamp;
} {
  if (period === "all") return {};

  const now = new Date();
  const end = Timestamp.fromDate(now);

  let start: Date;
  switch (period) {
    case "1month":
      start = subMonths(now, 1);
      break;
    case "3months":
      start = subMonths(now, 3);
      break;
    case "6months":
      start = subMonths(now, 6);
      break;
    case "1year":
      start = subMonths(now, 12);
      break;
  }

  return { start: Timestamp.fromDate(start), end };
}

function formatDate(timestamp: Timestamp | Date | undefined): string {
  if (!timestamp) return "N/A";
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
  return format(date, "d 'de' MMMM, yyyy", { locale: es });
}

export function AttendanceHistoryModal({
  isOpen,
  onClose,
  member,
  personName,
}: AttendanceHistoryModalProps) {
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>("3months");

  const { data: attendances = [], isLoading: loadingAttendances } =
    useAttendanceByMember(member?.id);
  const { isLoading: loadingStats } = useMemberAttendanceStats(member?.id);

  // Filtrar asistencias por período
  const filteredAttendances = useMemo(() => {
    if (!attendances || filterPeriod === "all") return attendances;

    const { start } = getDateRange(filterPeriod);
    if (!start) return attendances;

    return attendances.filter((a) => {
      const date =
        a.date instanceof Timestamp
          ? a.date
          : Timestamp.fromDate(new Date(a.date));
      return date.toMillis() >= start.toMillis();
    });
  }, [attendances, filterPeriod]);

  // Calcular estadísticas filtradas
  const filteredStats = useMemo(() => {
    const present = filteredAttendances.filter(
      (a) => a.status === "present",
    ).length;
    const absent = filteredAttendances.filter(
      (a) => a.status === "absent",
    ).length;
    const late = filteredAttendances.filter((a) => a.status === "late").length;
    const excused = filteredAttendances.filter(
      (a) => a.status === "excused",
    ).length;
    const total = filteredAttendances.length;
    const attendanceRate = total > 0 ? ((present + late) / total) * 100 : 0;

    return { present, absent, late, excused, total, attendanceRate };
  }, [filteredAttendances]);

  // Ordenar asistencias por fecha descendente
  const sortedAttendances = useMemo(() => {
    return [...filteredAttendances].sort((a, b) => {
      const dateA =
        a.date instanceof Timestamp
          ? a.date.toMillis()
          : new Date(a.date).getTime();
      const dateB =
        b.date instanceof Timestamp
          ? b.date.toMillis()
          : new Date(b.date).getTime();
      return dateB - dateA;
    });
  }, [filteredAttendances]);

  // Exportar a Excel
  const exportToCSV = () => {
    exportToXlsx({
      filename: `asistencia_${personName.replace(/\s/g, "_")}_${format(new Date(), "yyyy-MM-dd")}`,
      sheetName: "Asistencia",
      rows: sortedAttendances,
      columns: [
        { header: "Fecha", key: (a) => formatDate(a.date), width: 16 },
        {
          header: "Tipo",
          key: (a) => (a.meetingId ? "Reunión" : "Evento"),
          width: 10,
        },
        {
          header: "Estado",
          key: (a) => STATUS_CONFIG[a.status]?.label || a.status,
          width: 14,
        },
        {
          header: "Observaciones",
          key: (a) => a.justification || "",
          width: 32,
        },
      ],
    });
  };

  if (!isOpen || !member) return null;

  const isLoading = loadingAttendances || loadingStats;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Historial de Asistencia</h2>
              <p className="text-sm text-gray-500">{personName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Filtros y acciones */}
            <div className="p-4 border-b flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Período:</span>
                <Select
                  value={filterPeriod}
                  onChange={(e) =>
                    setFilterPeriod(e.target.value as FilterPeriod)
                  }
                  className="w-40"
                >
                  <option value="1month">Último mes</option>
                  <option value="3months">Últimos 3 meses</option>
                  <option value="6months">Últimos 6 meses</option>
                  <option value="1year">Último año</option>
                  <option value="all">Todo</option>
                </Select>
              </div>
              <Button
                variant="secondary"
                onClick={exportToCSV}
                disabled={filteredAttendances.length === 0}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exportar Excel
              </Button>
            </div>

            {/* Estadísticas */}
            <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Card className="bg-gray-50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-3xl font-bold text-gray-900">
                    {filteredStats.total}
                  </p>
                  <p className="text-xs text-gray-500">Total</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {filteredStats.present}
                  </p>
                  <p className="text-xs text-green-700">Presentes</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-3xl font-bold text-red-600">
                    {filteredStats.absent}
                  </p>
                  <p className="text-xs text-red-700">Ausentes</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-3xl font-bold text-yellow-600">
                    {filteredStats.late}
                  </p>
                  <p className="text-xs text-yellow-700">Tardanzas</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-3xl font-bold text-blue-600">
                      {filteredStats.attendanceRate.toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-xs text-blue-700">Asistencia</p>
                </CardContent>
              </Card>
            </div>

            {/* Lista de asistencias */}
            <div className="flex-1 overflow-y-auto p-4">
              {sortedAttendances.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Calendar className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="font-medium">
                    No hay registros de asistencia en este período
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedAttendances.map((attendance) => (
                    <div
                      key={attendance.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3">
                        {STATUS_CONFIG[attendance.status]?.icon}
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatDate(attendance.date)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {attendance.meetingId ? "Reunión" : "Evento"}
                            {attendance.justification &&
                              ` • ${attendance.justification}`}
                          </p>
                        </div>
                      </div>
                      <Badge
                        className={STATUS_CONFIG[attendance.status]?.color}
                      >
                        {STATUS_CONFIG[attendance.status]?.label}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end p-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
